import { useMemo } from "react";
import { useCurrentLead } from "../../hooks/useCurrentLead";
import { useLeadDerivedFields } from "../../hooks/useLeadDerivedFields";
import type { Lead } from "../../types/lead";
import { deriveValidationIssues } from "@/lib/lead/validation/leadValidation";
import { textHeuristics } from "@/lib/lead/extraction/textHeuristics";
import { FullLeadView } from "./FullLeadView";
import { CompactLeadView } from "./CompactLeadView";

type UseLeadWorkspaceViewModelParams = {
  currentLead: Lead | null;
  locationName?: string | null;
  locationAddress?: string | null;
  compactView: boolean;
  phoneDraft: string;
  setPhoneDraft: (next: string) => void;
  showProductDetails: boolean;
  setShowProductDetails: (next: boolean) => void;
  showLocationDetails: boolean;
  setShowLocationDetails: (next: boolean) => void;
  updateCurrentLeadField: (field: any, value: any) => void;
  buildGhlPayload: (lead: Lead) => any;
  onOpenSalesConsole?: () => void;
  createFollowUp: (leadId: string, draft: { next_action: string; due_date: string; reason: string }) => void;
  onLinkedOpportunity: (opportunityId: string) => void;
  onViewOpportunity: (opportunityId: string) => void;
  setToast: (t: { message: string; type: "success" | "error" } | null) => void;
};

export function useLeadWorkspaceViewModel(params: UseLeadWorkspaceViewModelParams) {
  const todayStr = new Date().toDateString();
  const isToday = (ts: number) => new Date(ts).toDateString() === todayStr;

  const lastUpdatedDisplay =
    params.currentLead && params.currentLead.lastUpdated
      ? new Date(params.currentLead.lastUpdated).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })
      : null;

  const lead = useCurrentLead({
    extracted: params.currentLead?.extracted,
    updateCurrentLeadField: params.updateCurrentLeadField as any,
    phoneDraft: params.phoneDraft,
    setPhoneDraft: params.setPhoneDraft as any,
    showProductDetails: params.showProductDetails,
    setShowProductDetails: params.setShowProductDetails as any,
    showLocationDetails: params.showLocationDetails,
    setShowLocationDetails: params.setShowLocationDetails as any,
  });

  const e = lead.e;
  const derived = useLeadDerivedFields({
    currentLeadName: params.currentLead?.name || "",
    e,
    locationName: params.locationName || null,
    locationAddress: params.locationAddress || null,
    leadId: params.currentLead?.id || null,
    leadLastUpdated: params.currentLead?.lastUpdated,
    isTest: params.currentLead?.isTest,
    sent: params.currentLead?.sent || false,
    queued: params.currentLead?.queued || false,
  });

  const leadView = !params.currentLead ? null : !params.compactView ? (
    <FullLeadView
      lead={lead}
      derived={derived}
      currentLead={params.currentLead}
      onOpenSalesConsole={params.onOpenSalesConsole}
      createFollowUp={params.createFollowUp}
      onLinkedOpportunity={params.onLinkedOpportunity}
      onViewOpportunity={params.onViewOpportunity}
      setToast={params.setToast}
    />
  ) : (
    <CompactLeadView
      currentLead={params.currentLead}
      leadStatusClass={derived.leadStatusClass}
      leadStatusLabel={derived.leadStatusLabel}
      leadAgeDisplay={derived.leadAgeDisplay}
    />
  );

  const leadSummaryPayload = params.currentLead?.extracted ? params.buildGhlPayload(params.currentLead) : null;
  const leadSummaryBuddyTags = leadSummaryPayload?.bb_tags ?? [];
  const leadSummaryValidationIssues = params.currentLead?.extracted
    ? deriveValidationIssues(params.currentLead.extracted, {
        normalizePhone: textHeuristics.normalizePhone,
        isValidEmail: textHeuristics.isValidEmail,
      })
    : [];
  const leadSummaryCanSend = leadSummaryValidationIssues.length === 0 && !params.currentLead?.isTest;

  return useMemo(
    () => ({
      isToday,
      lastUpdatedDisplay,
      derived,
      leadView,
      leadSummaryPayload,
      leadSummaryBuddyTags,
      leadSummaryValidationIssues,
      leadSummaryCanSend,
    }),
    [leadView, leadSummaryBuddyTags, leadSummaryCanSend, leadSummaryPayload, leadSummaryValidationIssues, lastUpdatedDisplay, derived, params.createFollowUp]
  );
}
